/**
 * The prices a counter may charge without asking, and the zones they are set by.
 *
 * A fee inside the range for its job is booked as it stands; one outside it,
 * or nothing at all, goes to the approvals queue with the counter's reason. The
 * most specific price wins — zone at a location, then zone, then location, then
 * business-wide — so an owner can price a far-off area differently without
 * touching the rest.
 *
 * Prices are dated, not edited: a changed price is a new one that starts on a
 * day, and the one before it ends the day before. So what a delivery was
 * checked against last month stays answerable, and nothing is set backwards.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DELIVERY_SERVICE_TYPES,
  type DeliveryPriceRule,
  type DeliveryZone,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { date, money, todayIn } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Spinner,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';

const STATUS_LABEL: Record<DeliveryPriceRule['status'], string> = {
  scheduled: 'Scheduled',
  current: 'Current',
  ended: 'Ended',
  withdrawn: 'Withdrawn',
};

const STATUS_TONE: Record<DeliveryPriceRule['status'], 'info' | 'success' | 'neutral' | 'warning'> = {
  scheduled: 'info',
  current: 'success',
  ended: 'neutral',
  withdrawn: 'warning',
};

/** A calendar day (YYYY-MM-DD) as people read it. Already in business time. */
function day(value: string | null): string {
  return value ? date(value.slice(0, 10), 'UTC') : '—';
}

/** Turn an API error's per-field messages into one message per field. */
function firstFieldErrors(caught: ApiError): Record<string, string> {
  return Object.fromEntries(
    Object.entries(caught.fields ?? {}).map(([k, v]) => [k, v[0] ?? '']),
  );
}

/** Zones offered at a location: its own, plus the ones offered everywhere. */
export function zonesAt(zones: DeliveryZone[], branchId: string): DeliveryZone[] {
  return zones.filter((z) => z.branchId === null || z.branchId === branchId);
}

export function PriceRulesPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const { locations } = useWorkingLocation();
  const currency = business?.currencyCode ?? 'NGN';
  const today = todayIn(business?.timezone ?? 'UTC');
  const mayManage = can('delivery_pricing:manage');

  const [showHistory, setShowHistory] = useState(false);
  const [form, setForm] = useState({
    serviceType: DELIVERY_SERVICE_TYPES[0] as string,
    branchId: '',
    zoneId: '',
    minFee: '',
    maxFee: '',
    effectiveFrom: '',
    note: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [zoneForm, setZoneForm] = useState({ name: '', branchId: '', description: '' });
  const [zoneFieldErrors, setZoneFieldErrors] = useState<Record<string, string>>({});
  const [zoneError, setZoneError] = useState<string | null>(null);

  const set =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [key]: event.target.value }));

  const setZoneField =
    <K extends keyof typeof zoneForm>(key: K) =>
    (event: { target: { value: string } }) =>
      setZoneForm((f) => ({ ...f, [key]: event.target.value }));

  const query = useQuery({
    queryKey: ['delivery-price-rules', business?.businessId, showHistory ? 'history' : 'live'],
    queryFn: () =>
      api.get<{ data: DeliveryPriceRule[] }>(
        path(`/deliveries/price-rules${showHistory ? '?history=true' : ''}`),
      ),
    enabled: Boolean(business) && mayManage,
  });

  const zonesQuery = useQuery({
    queryKey: ['delivery-zones', business?.businessId],
    queryFn: () => api.get<{ data: DeliveryZone[] }>(path('/deliveries/zones')),
    enabled: Boolean(business) && mayManage,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['delivery-price-rules'] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.post(path('/deliveries/price-rules'), {
        serviceType: form.serviceType,
        branchId: form.branchId || null,
        zoneId: form.zoneId || null,
        minFee: form.minFee,
        maxFee: form.maxFee,
        ...(form.effectiveFrom ? { effectiveFrom: form.effectiveFrom } : {}),
        ...(form.note.trim() ? { note: form.note.trim() } : {}),
      }),
    onSuccess: () => {
      setForm((f) => ({ ...f, minFee: '', maxFee: '', effectiveFrom: '', note: '' }));
      refresh();
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(firstFieldErrors(caught));
      } else {
        setError('Could not save this price.');
      }
    },
  });

  const retire = useMutation({
    mutationFn: (id: string) => api.post(path(`/deliveries/price-rules/${id}/retire`), {}),
    onSuccess: refresh,
    onError: (caught) =>
      setListError(caught instanceof ApiError ? caught.message : 'Could not change that price.'),
  });

  const refreshZones = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['delivery-zones'] });
  };

  const addZone = useMutation({
    mutationFn: () =>
      api.post(path('/deliveries/zones'), {
        name: zoneForm.name.trim(),
        branchId: zoneForm.branchId || null,
        ...(zoneForm.description.trim() ? { description: zoneForm.description.trim() } : {}),
      }),
    onSuccess: () => {
      setZoneForm({ name: '', branchId: '', description: '' });
      refreshZones();
    },
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setZoneError(caught.message);
        setZoneFieldErrors(firstFieldErrors(caught));
      } else {
        setZoneError('Could not save this zone.');
      }
    },
  });

  const retireZone = useMutation({
    mutationFn: (id: string) => api.post(path(`/deliveries/zones/${id}/retire`), {}),
    onSuccess: () => {
      refreshZones();
      refresh();
    },
    onError: (caught) =>
      setZoneError(caught instanceof ApiError ? caught.message : 'Could not retire that zone.'),
  });

  if (!mayManage) {
    return (
      <>
        <PageHeader title="Delivery prices" />
        <Card>
          <EmptyState
            title="Prices are set by the owner"
            description="Ask the owner or a manager to change what a delivery may cost."
          />
        </Card>
      </>
    );
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    add.mutate();
  }

  function submitZone(event: FormEvent): void {
    event.preventDefault();
    setZoneError(null);
    setZoneFieldErrors({});
    addZone.mutate();
  }

  const rules = query.data?.data ?? [];
  const zones = zonesQuery.data?.data ?? [];
  const zoneOptions = zonesAt(zones, form.branchId);

  return (
    <>
      <PageHeader
        title="Delivery prices"
        description="What each job may cost without asking. Anything outside goes to you for approval."
      />

      <Card>
        <CardHeader
          title={showHistory ? 'All prices' : 'Approved prices'}
          action={
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showHistory}
                onChange={(e) => setShowHistory(e.target.checked)}
              />
              Show history
            </label>
          }
        />
        {listError && (
          <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{listError}</p>
          </div>
        )}
        {query.isLoading && <Spinner label="Loading prices" />}
        {query.isError && <ErrorState message="Could not load prices." />}
        {query.data && rules.length === 0 && (
          <EmptyState
            title="No prices set yet"
            description="Until there is one, every fee is sent to you for approval."
          />
        )}
        {rules.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3">Job</th>
                  <th className="py-2 pr-3">Location</th>
                  <th className="py-2 pr-3">Zone</th>
                  <th className="py-2 pr-3">Price</th>
                  <th className="py-2 pr-3">Starts</th>
                  <th className="py-2 pr-3">Ends</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => {
                  const action =
                    r.status === 'current' ? 'End' : r.status === 'scheduled' ? 'Withdraw' : null;
                  return (
                    <tr key={r.id} className="border-b border-slate-100 align-top">
                      <td className="py-2 pr-3 font-medium text-slate-900">{r.serviceType}</td>
                      <td className="py-2 pr-3">{r.branchName ?? 'All locations'}</td>
                      <td className="py-2 pr-3">{r.zoneName ?? 'Any zone'}</td>
                      <td className="py-2 pr-3 tabular-nums">
                        {money(r.minFee, currency)} – {money(r.maxFee, currency)}
                        {r.note && <div className="text-xs text-slate-500">{r.note}</div>}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{day(r.effectiveFrom)}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {r.status === 'withdrawn' ? '—' : day(r.effectiveTo)}
                      </td>
                      <td className="py-2 pr-3">
                        <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                      </td>
                      <td className="py-2 text-right">
                        {action && mayManage && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setListError(null);
                              retire.mutate(r.id);
                            }}
                            loading={retire.isPending && retire.variables === r.id}
                          >
                            {action}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="mt-4">
        <CardHeader
          title="Add a price"
          subtitle="A new price ends the one it replaces on the day before it starts. Prices can't start before today."
        />
        <form onSubmit={submit} className="space-y-4" noValidate>
          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Select
              label="Job"
              required
              options={DELIVERY_SERVICE_TYPES.map((t) => ({ value: t, label: t }))}
              value={form.serviceType}
              onChange={set('serviceType')}
              error={fieldErrors['serviceType']}
            />
            <Select
              label="Location"
              options={[
                { value: '', label: 'All locations' },
                ...locations.map((b) => ({ value: b.id, label: b.name })),
              ]}
              value={form.branchId}
              onChange={(e) => {
                const branchId = e.target.value;
                // A zone only offered somewhere else cannot be priced here.
                setForm((f) => ({
                  ...f,
                  branchId,
                  zoneId: zonesAt(zones, branchId).some((z) => z.id === f.zoneId) ? f.zoneId : '',
                }));
              }}
              error={fieldErrors['branchId']}
            />
            <Select
              label="Zone"
              options={[
                { value: '', label: 'Any zone' },
                ...zoneOptions.map((z) => ({ value: z.id, label: z.name })),
              ]}
              value={form.zoneId}
              onChange={set('zoneId')}
              error={fieldErrors['zoneId']}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label={`Lowest (${currency})`}
              required
              inputMode="decimal"
              value={form.minFee}
              onChange={set('minFee')}
              error={fieldErrors['minFee']}
            />
            <Input
              label={`Highest (${currency})`}
              required
              inputMode="decimal"
              value={form.maxFee}
              onChange={set('maxFee')}
              error={fieldErrors['maxFee']}
            />
            <Input
              label="Starts"
              type="date"
              min={today}
              value={form.effectiveFrom}
              onChange={set('effectiveFrom')}
              error={fieldErrors['effectiveFrom']}
              hint="Leave empty to start today."
            />
          </div>
          <Input label="Note (optional)" value={form.note} onChange={set('note')} />
          <Button type="submit" loading={add.isPending}>
            Save price
          </Button>
        </form>
      </Card>

      <Card className="mt-4">
        <CardHeader
          title="Zones"
          subtitle="Areas you price separately. A zone at one location is only offered there."
        />
        {zoneError && (
          <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{zoneError}</p>
          </div>
        )}
        {zonesQuery.isLoading && <Spinner label="Loading zones" />}
        {zonesQuery.isError && <ErrorState message="Could not load zones." />}
        {zonesQuery.data && zones.length === 0 && (
          <p className="text-sm text-slate-500">No zones yet. Prices apply to every area.</p>
        )}
        {zones.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {zones.map((z) => (
              <li key={z.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <div className="text-sm">
                  <div className="font-medium text-slate-900">
                    {z.name} · {z.branchName ?? 'All locations'}
                  </div>
                  {z.description && <div className="text-slate-600">{z.description}</div>}
                </div>
                {mayManage && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setZoneError(null);
                      retireZone.mutate(z.id);
                    }}
                    loading={retireZone.isPending && retireZone.variables === z.id}
                  >
                    Retire
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {mayManage && (
          <form onSubmit={submitZone} className="mt-4 space-y-4" noValidate>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Zone name"
                required
                value={zoneForm.name}
                onChange={setZoneField('name')}
                error={zoneFieldErrors['name']}
              />
              <Select
                label="Zone location"
                options={[
                  { value: '', label: 'All locations' },
                  ...locations.map((b) => ({ value: b.id, label: b.name })),
                ]}
                value={zoneForm.branchId}
                onChange={setZoneField('branchId')}
                error={zoneFieldErrors['branchId']}
              />
            </div>
            <Input
              label="Description (optional)"
              value={zoneForm.description}
              onChange={setZoneField('description')}
              error={zoneFieldErrors['description']}
            />
            <Button type="submit" loading={addZone.isPending}>
              Add zone
            </Button>
          </form>
        )}
      </Card>
    </>
  );
}
